import moment from 'moment';
import './momentLocales';
import * as React from 'react';
import { format } from './StringExtensions';
import { format as dateFnsFormat } from 'date-fns';
import parse from 'date-fns/parse';
import { enUS, fi, sv } from 'date-fns/locale';
import formatInTimeZone from 'date-fns-tz/formatInTimeZone';
import Api from './Api';
import CountryService from './CountryService';
import { markTranslation, stripTranslationKeyMarkers } from './translationKeyMarkers';

class LanguageService {
    public static Instance: LanguageService = new LanguageService();
    public static CurrentLanguage: any;
    private static draftTranslation: { key: string; text: string } | null = null;
    private static keyMarkersPolicy: () => boolean = () => false;
    public static showTranslationKeys: boolean = false;
    private static currentLanguageLocalStorageKey: string = 'currentLanguage';

    private static IsInitiated: boolean = false;
    private static translationChangeListeners: Array<() => void> = [];

    constructor() {
        if (!LanguageService.Instance) {
            LanguageService.Instance = this;
        }
    }

    public static init(
        requestedLanguage: string = null,
        applicationVersion: string = null,
        translationsVersion: string = null
    ) {
        this.IsInitiated = true;

        return new Promise<any>((resolve: any, reject: any) => {
            if (
                (!requestedLanguage && LanguageService.CurrentLanguage) ||
                (requestedLanguage &&
                    LanguageService.CurrentLanguage &&
                    LanguageService.CurrentLanguage.key === requestedLanguage &&
                    (LanguageService.CurrentLanguage.applicationVersion === applicationVersion ||
                        applicationVersion === null) &&
                    (LanguageService.CurrentLanguage.translationsVersion === translationsVersion ||
                        translationsVersion === null))
            ) {
                resolve(LanguageService.CurrentLanguage);
                return;
            }

            let languageInLocalStorage = this.getLanguageInLocaleStorage();

            if (
                languageInLocalStorage &&
                (!requestedLanguage ||
                    (requestedLanguage === languageInLocalStorage.key &&
                        (languageInLocalStorage.applicationVersion === applicationVersion ||
                            applicationVersion === null) &&
                        (languageInLocalStorage.translationsVersion === translationsVersion ||
                            translationsVersion === null)))
            ) {
                resolve(languageInLocalStorage);
                this.setLanguage(languageInLocalStorage);
                return;
            }

            let browserLanguageKey = requestedLanguage || this.getBrowserLanguageKey();

            return this.getLanguageFromApi(browserLanguageKey)
                .then((language) => {
                    this.setLanguage(language);
                    this.setLanguageInLocaleStorage(language);
                    resolve(language);
                })
                .catch(reject);
        });
    }

    public static translate(key: string, properties?: {}) {
        if (!LanguageService.IsInitiated) {
            LanguageService.init();
        }

        const currentLanguage = LanguageService.CurrentLanguage;

        if (!currentLanguage) {
            return undefined;
        }

        if (LanguageService.showTranslationKeys) {
            return markTranslation(key, key);
        }

        const draft = LanguageService.draftTranslation;
        const value = LanguageService.formatTranslation(
            key,
            draft && draft.key === key ? draft.text : currentLanguage[key],
            properties
        );

        return LanguageService.keyMarkersPolicy() && typeof value === 'string'
            ? markTranslation(value, key)
            : value;
    }

    public static translatePlain(key: string, properties?: {}) {
        const currentLanguage = LanguageService.CurrentLanguage;
        if (!currentLanguage) {
            return undefined;
        }

        return LanguageService.formatTranslation(key, currentLanguage[key], properties);
    }

    private static formatTranslation(key: string, value: string | undefined, properties?: {}) {
        if (!value) {
            let errorStyle = { color: 'red' };
            return <span style={errorStyle}>{key}</span>;
        }

        return properties ? format(value, properties) : value;
    }

    public static setLanguage(language: any) {
        LanguageService.CurrentLanguage = language;
        LanguageService.notifyTranslationChanges();
    }

    public static subscribeToTranslationChanges(listener: () => void): () => void {
        LanguageService.translationChangeListeners = [
            ...LanguageService.translationChangeListeners,
            listener
        ];
        return () => {
            LanguageService.translationChangeListeners =
                LanguageService.translationChangeListeners.filter(
                    (subscribed) => subscribed !== listener
                );
        };
    }

    private static notifyTranslationChanges() {
        LanguageService.translationChangeListeners.forEach((listener) => listener());
    }

    public static getLanguage(): any {
        return LanguageService.CurrentLanguage;
    }

    public static setDraftTranslation(key: string, text: string) {
        LanguageService.draftTranslation = { key, text };
        LanguageService.notifyTranslationChanges();
    }

    public static clearDraftTranslation() {
        LanguageService.draftTranslation = null;
        LanguageService.notifyTranslationChanges();
    }

    public static isTranslationOf(text: string, key: string): boolean {
        const currentLanguage = LanguageService.CurrentLanguage;
        return (
            !!currentLanguage &&
            typeof text === 'string' &&
            stripTranslationKeyMarkers(text) === currentLanguage[key]
        );
    }

    public static setKeyMarkersPolicy(policy: () => boolean) {
        LanguageService.keyMarkersPolicy = policy;
        LanguageService.notifyTranslationChanges();
    }

    public static setShowTranslationKeys(enabled: boolean) {
        LanguageService.showTranslationKeys = enabled;
        LanguageService.notifyTranslationChanges();
    }

    public static updateTranslation(key: string, translation: string) {
        if (!LanguageService.CurrentLanguage) {
            return;
        }

        LanguageService.CurrentLanguage = {
            ...LanguageService.CurrentLanguage,
            [key]: translation
        };
        this.clearLanguageInLocaleStorage();
        LanguageService.notifyTranslationChanges();
    }

    public static getLanguageFromApi(languageKey: string) {
        return new Api().get('/language/' + languageKey);
    }

    public static getBrowserLanguageKey(): string {
        let key = navigator.language;
        if (!key) {
            key = navigator['browserLanguage'];
        }

        return key.toLowerCase().substring(0, 2);
    }

    public static setLanguageInLocaleStorage(languageValue: any) {
        try {
            if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
                return;
            }

            window.localStorage.setItem(
                this.currentLanguageLocalStorageKey,
                JSON.stringify(languageValue)
            );
        } catch {
            // Ignore storage errors (e.g. blocked storage, unsupported runtime)
        }
    }

    private static clearLanguageInLocaleStorage() {
        try {
            if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
                return;
            }

            window.localStorage.removeItem(this.currentLanguageLocalStorageKey);
        } catch {
            // Ignore storage errors (e.g. blocked storage, unsupported runtime)
        }
    }

    private static getLanguageInLocaleStorage(): any {
        try {
            if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
                return null;
            }

            let language = window.localStorage.getItem(this.currentLanguageLocalStorageKey);
            return language !== null ? JSON.parse(language) : null;
        } catch {
            return null;
        }
    }
}

export function setMomentLocale(locale?: string) {
    if (!locale) {
        locale = LanguageService.getBrowserLanguageKey();
    }

    moment.locale(locale);
}

export const SUPPORTED_DATE_LOCALES = {
    en: enUS,
    fi: fi,
    sv: sv
};

export function getSupportedDateLocal(locale: string) {
    return SUPPORTED_DATE_LOCALES[locale]
        ? SUPPORTED_DATE_LOCALES[locale]
        : SUPPORTED_DATE_LOCALES['en'];
}

export function setDateLocale(locale?: string) {
    window.__dateLocale = locale ? locale : LanguageService.getBrowserLanguageKey();
}

export function formatDate(date: Date, formatStr: string): string {
    let defaultCountryId = CountryService.getDefaultOrImpersonatedCountryId();
    const userTimeZone = countryToTimeZone[defaultCountryId] || 'Europe/Stockholm';

    return formatInTimeZone(date, userTimeZone, formatStr);
}

export function translateDateFormat(date: Date, formatStr: string): string {
    let locale = window.__dateLocale || LanguageService.getBrowserLanguageKey();

    return dateFnsFormat(date, formatStr, { locale: getSupportedDateLocal(locale) });
}

export function parseTranslatedDate(date: string, formatStr: string): Date {
    let locale = window.__dateLocale || LanguageService.getBrowserLanguageKey();

    return parse(date, formatStr, new Date(), { locale: getSupportedDateLocal(locale) });
}

const countryToTimeZone = {
    '1': 'Europe/Stockholm',
    '2': 'Europe/Helsinki',
    '3': 'Europe/Paris',
    '4': 'Europe/Madrid'
};

export function GetLanguageForKey(key: string) {
    return LanguageService.getLanguageFromApi(key);
}

export function Translate(key: string, properties?: {}): string {
    return LanguageService.translate(key, properties);
}

export function TranslatePlain(key: string, properties?: {}): string {
    return LanguageService.translatePlain(key, properties);
}

export function isTranslationOf(text: string, key: string): boolean {
    return LanguageService.isTranslationOf(text, key);
}

export default LanguageService;
